import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Stack, Chip,
  IconButton, TextField, MenuItem, Tooltip, Avatar, LinearProgress,
  Divider, Paper
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon,
  Business as BusinessIcon, Domain as DomainIcon,
  ArrowBack as ArrowBackIcon, Email as EmailIcon, Phone as PhoneIcon,
  Language as WebIcon, LocationOn as LocationIcon,
  Architecture as ArchiIcon, Engineering as EngIcon,
  Apartment as PromoIcon
} from '@mui/icons-material';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { crmApi } from '../services/crmApi';
import { CrmOrganisation, OrganisationType, OrganisationSpecialty, OrganisationSize } from '../types/crm';

const emptyOrganisation = (): Partial<CrmOrganisation> => ({
  name: '',
  type: 'BET',
  specialty: 'RESIDENTIEL',
  size: 'MOYEN',
  zoneGeo: 'Alger',
  address: '',
  phone: '',
  email: '',
  website: '',
  history: ''
});

const getTypeColor = (type: OrganisationType): 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'default' => {
  switch (type) {
    case 'BET': return 'info';
    case 'ARCHITECTE': return 'secondary';
    case 'PROMOTEUR': return 'warning';
    case 'MO': return 'primary';
    case 'ENT': return 'success';
    default: return 'default';
  }
};

const getTypeIcon = (type: OrganisationType) => {
  switch (type) {
    case 'BET': return <EngIcon fontSize="small" />;
    case 'ARCHITECTE': return <ArchiIcon fontSize="small" />;
    case 'PROMOTEUR': return <PromoIcon fontSize="small" />;
    default: return <DomainIcon fontSize="small" />;
  }
};

export const OrganisationsPage: React.FC = () => {
  const { t } = useLanguage();

  const [view, setView] = useState<'list' | 'form'>('list');
  const [viewMode, setViewMode] = useDefaultViewMode();
  const [organisationsList, setOrganisationsList] = useState<CrmOrganisation[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<CrmOrganisation>>(emptyOrganisation());
  const [isEditing, setIsEditing] = useState(false);

  const loadOrganisations = async () => {
    setLoading(true);
    try {
      const data = await crmApi.getOrganisations();
      setOrganisationsList(data);
    } catch (err) {
      console.error('Error loading CRM organisations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrganisations();
  }, []);

  const openNew = () => {
    setFormData(emptyOrganisation());
    setIsEditing(false);
    setView('form');
  };

  const openEdit = (org: CrmOrganisation) => {
    setFormData({ ...org, history: org.history || '' });
    setIsEditing(true);
    setView('form');
  };

  const backToList = () => {
    setView('list');
  };

  const handleDelete = async (org: CrmOrganisation) => {
    const orgId = (org.id || org.COR_IDF)!;
    const confirmed = await blobticMessage.confirm({
      message: t('crm.organisations.confirmDelete', 'Êtes-vous sûr de vouloir supprimer cette organisation ?'),
      title: t('common.confirm', 'Confirmation')
    });

    if (confirmed) {
      const res = await crmApi.deleteOrganisation(orgId);
      if (res.success) {
        blobticMessage.success(t('crm.organisations.deleteSuccess', 'Organisation supprimée avec succès.'));
        loadOrganisations();
      } else {
        blobticMessage.error(res.message || t('common.error', 'Erreur lors de la suppression.'));
      }
    }
  };

  const handleValidate = (): string | null => {
    if (!formData.name?.trim()) {
      return t('crm.organisations.nameRequired', 'La raison sociale de l\'organisation est requise.');
    }
    return null;
  };

  const handleDataChange = () => {
    backToList();
    loadOrganisations();
  };

  if (view === 'form') {
    const formPayload = {
      COR_IDF: formData.id || formData.COR_IDF,
      COR_CODE: formData.code,
      COR_NAME: formData.name,
      COR_TYPE: formData.type,
      COR_SPECIALTY: formData.specialty,
      COR_SIZE: formData.size,
      COR_ZONE_GEO: formData.zoneGeo,
      COR_ADDRESS: formData.address,
      COR_PHONE: formData.phone,
      COR_EMAIL: formData.email,
      COR_WEBSITE: formData.website,
      COR_HISTORY: formData.history || '',
    };

    return (
      <GenericPage<any>
        title={isEditing ? t('crm.organisations.editTitle', 'Modifier l\'Organisation') : t('crm.organisations.newTitle', 'Nouvelle Organisation')}
        icon={<DomainIcon />}
        footerEnabled={true}
        footerInfo={formData.last_updt ? { lastUpdate: formData.last_updt, userLib: formData.user_lib } : undefined}
        additionalActions={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={backToList}
          >
            {t('common.backToList', 'Retour à la liste')}
          </Button>
        }
        crudConfig={{ entityKey: 'Blobtic_CRM,crm_organisation', primaryKey: 'COR_IDF' }}
        data={formPayload}
        onDataChange={handleDataChange}
        onValidate={handleValidate}
        onClear={() => setFormData(emptyOrganisation())}
      >
        <Box sx={{ p: 1 }}>
          <Grid container spacing={3}>
            {/* Section 1: Identification */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DomainIcon color="primary" /> {t('crm.organisations.secIdent', 'Identification de l\'Organisation')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('common.code', 'Code')}
                      value={formData.code || t('common.autoGenerated', '(Automatique)')}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      label={t('crm.organisations.name', 'Raison Sociale / Nom')}
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label={t('crm.organisations.type', 'Type d\'Organisation')}
                      value={formData.type || 'BET'}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as OrganisationType })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="BET">{t('crm.organisations.typeBet', 'Bureau d\'Études Techniques (BET)')}</MenuItem>
                      <MenuItem value="ARCHITECTE">{t('crm.organisations.typeArchitecte', 'Architecte / Concepteur')}</MenuItem>
                      <MenuItem value="PROMOTEUR">{t('crm.organisations.typePromoteur', 'Promoteur Immobilier')}</MenuItem>
                      <MenuItem value="MO">{t('crm.organisations.typeMo', 'Maître d\'Ouvrage (MO)')}</MenuItem>
                      <MenuItem value="ENT">{t('crm.organisations.typeEnt', 'Entreprise Générale (ENT)')}</MenuItem>
                      <MenuItem value="INSTALLATEUR">{t('crm.organisations.typeInstallateur', 'Installateur / Menuisier')}</MenuItem>
                      <MenuItem value="AUTRE">{t('crm.organisations.typeAutre', 'Autre Acteur')}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label={t('crm.organisations.specialty', 'Spécialité Principale')}
                      value={formData.specialty || 'RESIDENTIEL'}
                      onChange={(e) => setFormData({ ...formData, specialty: e.target.value as OrganisationSpecialty })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="RESIDENTIEL">{t('crm.organisations.specResidentiel', 'Résidentiel (Collectif & Promotion)')}</MenuItem>
                      <MenuItem value="TERTIAIRE">{t('crm.organisations.specTertiaire', 'Tertiaire & Bureaux')}</MenuItem>
                      <MenuItem value="INDUSTRIEL">{t('crm.organisations.specIndustriel', 'Industriel & Logistique')}</MenuItem>
                      <MenuItem value="MIXTE">{t('crm.organisations.specMixte', 'Mixte')}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label={t('crm.organisations.size', 'Envergure / Taille')}
                      value={formData.size || 'MOYEN'}
                      onChange={(e) => setFormData({ ...formData, size: e.target.value as OrganisationSize })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="PETIT">{t('crm.organisations.sizePetit', 'Petite structure (< 10 p.)')}</MenuItem>
                      <MenuItem value="MOYEN">{t('crm.organisations.sizeMoyen', 'Moyenne structure (10-50 p.)')}</MenuItem>
                      <MenuItem value="GRAND">{t('crm.organisations.sizeGrand', 'Grande envergure (> 50 p.)')}</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Section 2: Localisation & Coordonnées */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon color="primary" /> {t('crm.organisations.secCoords', 'Localisation & Coordonnées')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('crm.organisations.zoneGeo', 'Wilaya / Région')}
                      value={formData.zoneGeo || ''}
                      onChange={(e) => setFormData({ ...formData, zoneGeo: e.target.value })}
                      placeholder={t('crm.organisations.zoneGeoPlaceholder', 'Ex: Alger, Oran, Blida...')}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      label={t('common.address', 'Adresse Complète')}
                      value={formData.address || ''}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('common.phone', 'Téléphone')}
                      value={formData.phone || ''}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('common.email', 'Email Professionnel')}
                      value={formData.email || ''}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('crm.organisations.website', 'Site Web / Portfolio')}
                      value={formData.website || ''}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://..."
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Section 3: Historique & Partenariat */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon color="primary" /> {t('crm.organisations.secHistory', 'Historique & Partenariat')}
                </Typography>
                <TextField
                  label={t('crm.organisations.historyNotes', 'Notes de partenariat, projets antérieurs prescrits, préconisations')}
                  value={formData.history || ''}
                  onChange={(e) => setFormData({ ...formData, history: e.target.value })}
                  multiline
                  rows={4}
                  fullWidth
                  placeholder={t('crm.organisations.historyPlaceholder', 'Détaillez ici l\'historique relationnel, la fidélité, les préférences architecturales ou les gammes privilégiées...')}
                />
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </GenericPage>
    );
  }

  // List View
  return (
    <GenericPage
      title={t('crm.organisations.title', 'Écosystème Prescription & Organisations')}
      icon={<DomainIcon />}
      footerEnabled={false}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      onNew={openNew}
    >
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {viewMode === 'grid' ? (
        <Grid container spacing={2.5}>
          {organisationsList.map((org) => {
            const orgId = org.id || org.COR_IDF;
            return (
              <Grid item xs={12} sm={6} md={4} key={orgId}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    borderRadius: 2.5,
                    transition: 'all 0.25s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                      borderColor: 'primary.main',
                      transform: 'translateY(-3px)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44 }}>
                        {getTypeIcon(org.type)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700} noWrap title={org.name}>
                          {org.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {org.code} • {org.zoneGeo || 'Algérie'}
                        </Typography>
                      </Box>
                      <Chip
                        label={t(org.type, org.type)}
                        color={getTypeColor(org.type)}
                        size="small"
                        sx={{ fontWeight: 700 }}
                      />
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />

                    <Stack spacing={1} sx={{ mb: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={t(org.specialty, org.specialty)} size="small" variant="outlined" />
                        <Chip label={`${t('crm.organisations.size', 'Taille')}: ${t(org.size, org.size)}`} size="small" variant="outlined" />
                      </Stack>

                      {org.phone && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PhoneIcon fontSize="inherit" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {org.phone}
                          </Typography>
                        </Stack>
                      )}

                      {org.email && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <EmailIcon fontSize="inherit" color="action" />
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {org.email}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>

                    {org.history && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          bgcolor: 'action.hover',
                          p: 1,
                          borderRadius: 1
                        }}
                      >
                        {org.history}
                      </Typography>
                    )}
                  </CardContent>

                  <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {org.projectsCount || 0} {t('crm.organisations.projects', 'projet(s)')} • {org.contactsCount || 0} {t('crm.organisations.contacts', 'contact(s)')}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={t('common.edit', 'Modifier')}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(org)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete', 'Supprimer')}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(org)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <GenericTable
          columns={[
            { id: 'code', label: t('common.code', 'Code'), width: 110 },
            { id: 'name', label: t('crm.organisations.name', 'Organisation'), minWidth: 200 },
            {
              id: 'type',
              label: t('crm.organisations.type', 'Type'),
              width: 140,
              chip: true,
              chipColorMap: {
                BET: 'info',
                ARCHITECTE: 'secondary',
                PROMOTEUR: 'warning',
                MO: 'primary',
                ENT: 'success'
              }
            },
            { id: 'specialty', label: t('crm.organisations.specialty', 'Spécialité'), width: 140, chip: true },
            { id: 'size', label: t('crm.organisations.size', 'Taille'), width: 110, chip: true },
            { id: 'zoneGeo', label: t('crm.organisations.zoneGeo', 'Région'), width: 120 },
            { id: 'phone', label: t('common.phone', 'Téléphone'), width: 130 },
            { id: 'email', label: t('common.email', 'Email'), width: 180 },
          ]}
          data={organisationsList}
          onEdit={openEdit}
          onDelete={handleDelete}
          searchPlaceholder={t('crm.organisations.search', 'Rechercher une organisation (BET, Architecte, Promoteur)...')}
          filterColumns={['code', 'name', 'type', 'specialty', 'zoneGeo']}
          maxHeight="calc(100vh - 270px)"
        />
      )}
    </GenericPage>
  );
};

export default OrganisationsPage;
